import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountLayout from '../components/AccountLayout';
import AccountPagination, { AccountPaginationMeta } from '../components/AccountPagination';
import AccountSupportDialog, { AccountSupportTarget } from '../components/AccountSupportDialog';
import {
  MemberReservation,
  getMemberReservations,
  memberReservationStatusClass,
  memberReservationStatusLabels,
} from '@/services/memberReservations';

const defaultPagination: AccountPaginationMeta = { Page: 1, PerPage: 10, Total: 0, TotalPages: 1 };

export default function AccountReservations() {
  const navigate = useNavigate();
  const [items, setItems] = useState<MemberReservation[]>([]);
  const [pagination, setPagination] = useState<AccountPaginationMeta>(defaultPagination);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [supportTarget, setSupportTarget] = useState<AccountSupportTarget | null>(null);

  function load(page: number) {
    setLoading(true);
    setError('');
    getMemberReservations(page, defaultPagination.PerPage)
      .then((data) => {
        setItems(data.items);
        setPagination(data.pagination);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '預約紀錄載入失敗。');
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load(1);
  }, []);

  return (
    <AccountLayout title="預約紀錄">
      <p className="mb-4 text-sm text-[#9A8A7A]">這裡會顯示您以會員身份登入後送出的產品試戴預約與活動預約（例如聽力檢測、門市活動）。</p>

      {loading ? (
        <div className="rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] py-16 text-center text-sm text-[#9A8A7A]">載入中...</div>
      ) : error ? (
        <div className="rounded-2xl border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm text-[#c3423f]">{error}</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] py-16 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white">
            <i className="ri-calendar-check-line text-2xl text-[#9A8A7A]" />
          </div>
          <p className="font-bold text-[#1B3D2A]">目前尚無預約紀錄</p>
          <p className="mt-1 text-sm text-[#9A8A7A]">以會員身份登入後送出的預約會顯示在這裡。</p>
          <button
            type="button"
            onClick={() => navigate('/booking')}
            className="mt-5 rounded-xl bg-[#1B3D2A] px-6 py-3 text-sm font-bold text-white hover:bg-[#143026]"
          >
            立即預約
          </button>
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {items.map((item) => (
              <div key={item.rowKey} className="rounded-2xl border border-[#E8E0D6] bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-[#1B3D2A]/8 px-2.5 py-0.5 text-xs font-bold text-[#1B3D2A]">{item.typeLabel}</span>
                      <p className="text-sm font-bold text-[#1B3D2A]">{item.store}</p>
                    </div>
                    <p className="mt-1 text-xs text-[#9A8A7A]">{item.createdAt}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${memberReservationStatusClass[item.status] || ''}`}>
                    {memberReservationStatusLabels[item.status] || item.status}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-[#E8E0D6] pt-3 text-sm text-[#5A6B5A]">
                  {item.detail && (
                    <span className="flex items-center gap-1.5">
                      <i className="ri-headphone-line text-[#9A8A7A]" />
                      {item.detail}
                    </span>
                  )}
                  {(item.scheduleDate || item.scheduleTime) && (
                    <span className="flex items-center gap-1.5">
                      <i className="ri-time-line text-[#9A8A7A]" />
                      {[item.scheduleDate, item.scheduleTime].filter(Boolean).join(' ')}
                    </span>
                  )}
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setSupportTarget({
                      sourceType: item.sourceType,
                      sourceId: item.sourceId,
                      title: `${item.typeLabel} ${item.store}`,
                      description: [item.detail, item.scheduleDate, item.scheduleTime].filter(Boolean).join(' / '),
                      category: 'Reservation',
                    })}
                    className="rounded-xl border border-[#1B3D2A]/20 px-4 py-2 text-sm font-bold text-[#1B3D2A] hover:bg-[#1B3D2A]/8"
                  >
                    詢問這筆預約
                  </button>
                </div>
              </div>
            ))}
          </div>
          <AccountPagination disabled={loading} pagination={pagination} onChange={load} />
        </>
      )}
      <AccountSupportDialog target={supportTarget} onClose={() => setSupportTarget(null)} />
    </AccountLayout>
  );
}
